import './money-input.css';
import {
  currencyName,
  currencySymbol,
  formatAmountInput,
  formatDecimalInput,
  minorUnitDigits,
  normaliseCurrency,
  parseMoney,
  sanitiseAmountText,
} from '../model/finance/money.js';

let autoId = 0;

/**
 * A money field for modals: a `<label for>` (like `createField`) whose input
 * sits in a wrapper showing a permanent currency symbol ("AU$", "US$").
 *
 * - **Currency picker.** With more than one of `currencies`, the symbol gets a
 *   ▾ and a transparent native `<select>` laid over it (the platform picker,
 *   keyboard-operable, named "Currency"). The `<label>` names the AMOUNT input
 *   explicitly (`for`), so clicks on the select stay on the select and clicks
 *   anywhere else focus the amount (no multi-control label forwarding).
 * - **Only amount characters.** Typing / pasting keeps digits, grouping commas
 *   and one point (`sanitiseAmountText`): pasting "  $100.23    " shows
 *   "100.23". Fraction digits past `decimals` (default: the currency's minor
 *   digits) are blocked; "-" only with `allowNegative`.
 * - **Tidy on blur.** A valid amount is grouped ("90000" → "90,000",
 *   "12.5" → "12.50"); price fields (`decimals` above the currency's) keep up
 *   to `decimals` places ("213.4567").
 *
 * The input carries `data-currency` (the reader in pages/finance-fields.js
 * reads it when no currency is passed).
 *
 * @param {object} opts
 * @param {string} [opts.label] caption text
 * @param {string} [opts.name]
 * @param {string} [opts.id] the input's id (generated when absent)
 * @param {string} [opts.currency='AUD'] the initial currency
 * @param {string[]} [opts.currencies] the choosable currencies (picker when > 1)
 * @param {number} [opts.decimals] max fraction digits (e.g. 4 for share prices)
 * @param {boolean} [opts.allowNegative=false]
 * @param {string} [opts.placeholder]
 * @param {(currency: string) => void} [opts.onCurrencyChange] after the user picks
 * @returns {{ field: HTMLLabelElement, input: HTMLInputElement,
 *   select: HTMLSelectElement|null, getCurrency: () => string,
 *   setCurrency: (currency: string) => void,
 *   setCents: (cents: number|null) => void,
 *   setAmount: (amount: number|null) => void,
 *   setValue: (value: { amount: number, currency: string }|null) => void }}
 */
export function createMoneyField({
  label,
  name,
  id = name,
  currency = 'AUD',
  currencies,
  decimals,
  allowNegative = false,
  placeholder,
  onCurrencyChange,
} = {}) {
  let current = normaliseCurrency(currency);
  const choices = [...new Set([current, ...(currencies ?? []).map(normaliseCurrency)])];
  const picker = choices.length > 1;
  const inputId = id || `money-input-${++autoId}`;

  const field = document.createElement('label');
  field.htmlFor = inputId;
  field.append(document.createTextNode(label ?? ''));

  const wrap = document.createElement('span');
  wrap.className = picker ? 'money-input money-input--picker' : 'money-input';
  const prefix = document.createElement('span');
  prefix.className = 'money-input__prefix';
  prefix.setAttribute('aria-hidden', 'true');
  const symbol = document.createElement('span');
  symbol.className = 'money-input__symbol';
  prefix.append(symbol);
  if (picker) {
    const caret = document.createElement('i');
    caret.className = 'fa-solid fa-caret-down money-input__caret';
    prefix.append(caret);
  }

  let select = null;
  if (picker) {
    select = document.createElement('select');
    select.className = 'money-input__select';
    select.setAttribute('aria-label', 'Currency');
    for (const c of choices) {
      const option = document.createElement('option');
      option.value = c;
      option.textContent = `${currencySymbol(c)} — ${currencyName(c)}`;
      select.append(option);
    }
    select.addEventListener('change', () => {
      setCurrency(select.value);
      onCurrencyChange?.(current);
    });
  }

  const input = document.createElement('input');
  input.type = 'text';
  input.inputMode = 'decimal';
  input.autocomplete = 'off';
  input.id = inputId;
  if (name) input.name = name;
  if (placeholder !== undefined) input.placeholder = placeholder;

  const minorDigits = () => minorUnitDigits(current);
  const fractionDigits = () => decimals ?? minorDigits();
  const cleanOpts = () => ({ decimals: fractionDigits(), allowNegative });

  input.addEventListener('input', (e) => {
    const raw = input.value;
    const caret = input.selectionStart ?? raw.length;
    const opts = cleanOpts();
    // A typed digit past the allowed decimals is BLOCKED (the keystroke is
    // undone) rather than silently dropping some other digit.
    if (e.inputType === 'insertText' && e.data) {
      const unlimited = sanitiseAmountText(raw, { ...opts, decimals: Infinity });
      if (sanitiseAmountText(raw, opts) !== unlimited) {
        const at = Math.max(0, caret - e.data.length);
        input.value = raw.slice(0, at) + raw.slice(caret);
        input.setSelectionRange(at, at);
        return;
      }
    }
    const clean = sanitiseAmountText(raw, opts);
    if (clean === raw) return;
    input.value = clean;
    const pos = sanitiseAmountText(raw.slice(0, caret), opts).length;
    input.setSelectionRange(pos, pos);
  });

  input.addEventListener('blur', () => {
    if (fractionDigits() > minorDigits()) {
      const n = Number(input.value.replace(/,/g, ''));
      if (input.value.trim() && Number.isFinite(n) && (allowNegative || n >= 0)) {
        input.value = formatDecimalInput(n, {
          minDecimals: minorDigits(),
          maxDecimals: fractionDigits(),
        });
      }
      return;
    }
    const cents = parseMoney(input.value, current);
    if (cents !== null && (allowNegative || cents >= 0)) {
      input.value = formatAmountInput(cents, current);
    }
  });

  function setCurrency(next) {
    current = normaliseCurrency(next);
    const text = currencySymbol(current);
    symbol.textContent = text;
    // Room for the symbol (+ the ▾) inside the input's left padding. Set via
    // the CSSOM — the CSP forbids inline style attributes.
    wrap.style.setProperty('--money-prefix-width', `${text.length + (picker ? 1.4 : 0)}ch`);
    input.dataset.currency = current;
    if (select) select.value = current;
    // A currency with fewer decimals (e.g. JPY) trims what's typed.
    const clean = sanitiseAmountText(input.value, cleanOpts());
    if (clean !== input.value) input.value = clean;
  }
  setCurrency(current);

  wrap.append(prefix, ...(select ? [select] : []), input);
  field.append(wrap);
  return {
    field,
    input,
    select,
    getCurrency: () => current,
    setCurrency,
    setCents: (cents) => {
      input.value = formatAmountInput(cents, current);
    },
    setAmount,
    setValue,
  };

  function setAmount(amount) {
    input.value = formatDecimalInput(amount, {
      minDecimals: minorDigits(),
      maxDecimals: fractionDigits(),
    });
  }

  /**
   * Show a money value `{ amount, currency }` (null = blank, currency kept):
   * switches to its currency, then the amount — a decimal price for a price
   * field (`decimals` above the currency's), else minor units.
   */
  function setValue(value) {
    if (!value) {
      input.value = '';
      return;
    }
    setCurrency(value.currency);
    if (fractionDigits() > minorDigits()) setAmount(value.amount);
    else input.value = formatAmountInput(value.amount, current);
  }
}
